/* eslint-disable import/prefer-default-export */
/* eslint-disable no-nested-ternary */
import config from "@Config/index";
import { shallowEqual, useSelector } from "react-redux";
import useTranslationHook from "./useTranslationHook";
import { AppState } from "@Store/index";
import useWhitelabel from "./useWhitelabel";

interface InventoryMeta {
  cro_tags?: {
    amber_sale?: {
      status?: string;
      status_en?: string;
      offer?: string;
    };
  };
}

export const useAmberSales = ({
  inventoryMeta = {},
  inventory = null,
  page = "",
}: {
  inventoryMeta?: InventoryMeta;
  inventory?: any;
  page?: string;
}) => {
  const { t } = useTranslationHook();
  const { isWhitelabel } = useWhitelabel();
  const { searchPageAmberSales, propertyPageAmberSales } = useSelector(
    ({ propertyPageData, searchPageData }: AppState) => ({
      searchPageAmberSales: searchPageData?.region?.amber_sales,
      propertyPageAmberSales: propertyPageData?.data?.amber_sales,
    }),
    shallowEqual,
  );

  const amberSales =
    page === "SEARCH"
      ? searchPageAmberSales?.length > 0
        ? searchPageAmberSales[0]
        : null
      : page === "PROPERTY"
      ? propertyPageAmberSales?.length > 0
        ? propertyPageAmberSales[0]
        : null
      : inventory?.amber_sales?.[0];
  const regionId = amberSales?.meta?.ids || [];
  // Amber Sale is disabled for whitelabel partners — gating the master flag
  // closes every downstream value (banners, colors, inventory offer text and
  // isSaleOfferAvailableOnInventory all derive from it).
  const isSaleOfferAvailable = !isWhitelabel && Boolean(amberSales);
  let isSaleOfferAvailableOnInventory = isSaleOfferAvailable
    ? inventoryMeta?.cro_tags?.amber_sale?.status === "true" ||
      inventoryMeta?.cro_tags?.amber_sale?.status_en === "true"
    : false;

  if (
    page !== "HOME" &&
    isSaleOfferAvailableOnInventory &&
    inventory &&
    regionId.length > 0 &&
    inventory?.regionId != undefined &&
    !regionId.includes(inventory?.region_id)
  ) {
    isSaleOfferAvailableOnInventory = false;
  }
  const mainBannerImage =
    (isSaleOfferAvailable &&
      isNonAmberBrandKit(t("ambersale.banner.main_banner_desktop"), isWhitelabel)) ||
    "";
  const mainBannerImageMobile =
    (isSaleOfferAvailable &&
      isNonAmberBrandKit(t("ambersale.banner.main_banner_mobile"), isWhitelabel)) ||
    "";
  const inventoryBrandImage =
    (isSaleOfferAvailable &&
      brandImageForWhiteLabel(t("ambersale.banner.brand_image_transparent"), isWhitelabel)) ||
    "";

  const backgroundGradient = (isSaleOfferAvailable && amberSales?.data?.color_gradient) || "";

  const rawColor = (isSaleOfferAvailable && amberSales?.data?.color_primary) || "";
  // eslint-disable-next-line no-nested-ternary
  const primaryColor = rawColor.startsWith("#") ? rawColor : rawColor ? `#${rawColor}` : "";
  const borderColor =
    isSaleOfferAvailable && isSaleOfferAvailableOnInventory
      ? amberSales?.data?.border_color
      : "#FDD393";

  const salesOfferTextForInventory = isSaleOfferAvailableOnInventory
    ? inventoryMeta.cro_tags?.amber_sale?.offer ||
      t("searchpage.components.ambersales.defaultoffer")
    : "";
  return {
    amberSales,
    isSaleOfferAvailable,
    mainBannerImage,
    mainBannerImageMobile,
    inventoryBrandImage,
    backgroundGradient,
    primaryColor,
    borderColor,
    isSaleOfferAvailableOnInventory,
    salesOfferTextForInventory,
  };
};

export const isChinaSite = (link: string) => {
  let finalLink = link;
  if (config.IS_CHINA_APP) {
    const linkPart = link.split("https://static-assets.amberstudent.com/images/");
    finalLink = `https://static-assets.amberstudent.com/images/china-${linkPart[1]}`;
  }
  return finalLink || "";
};

export const isNonAmberBrandKit = (link: string, isUniversityPartner: boolean) => {
  if (!link || !isUniversityPartner) return link || "";
  // Replace amber-fest/{lang}/ with amber-fest/wl/ and prefix filename with "whitelabel-"

  const final = link.replace(/(\/amber-fest\/)([^/]+)(\/)(.*)/, "$1wl$3whitelabel-$4");
  return final;
};

export const brandImageForWhiteLabel = (link: string, isWhitelabel: boolean) => {
  if (isWhitelabel) link = link.replace(/(\/amber-fest\/)([^/]+)(\/)(.*)/, "$1wl$3whitelabel-$4");

  return link || "";
};
